import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "file",
    "headers",
    "colSep",
    "quoteChar",
    "liberal",
    "stripCsv",
    "rowSep",
    "previewHead",
    "previewBody",
    "error",
    "rawPreview"
  ]

  static values = { previewUrl: String, hasStoredFile: Boolean }

  connect() {
    this.timer = null
    this.refreshRawFromFile()
    this.schedulePreview()
  }

  schedulePreview() {
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.requestPreview(), 280)
  }

  refreshRawFromFile() {
    if (!this.hasRawPreviewTarget) return
    const f = this.fileTarget?.files?.[0]
    if (!f) {
      this.rawPreviewTarget.textContent = "—"
      return
    }
    const reader = new FileReader()
    reader.onload = () => {
      const text = String(reader.result || "")
      this.rawPreviewTarget.textContent = text.split(/\r?\n/).slice(0, 5).join("\n")
    }
    reader.readAsText(f.slice(0, 24_000))
  }

  requestPreview() {
    this.refreshRawFromFile()
    this.hideError()

    const uploaded = this.fileTarget?.files?.[0]
    if (!uploaded && !this.hasStoredFileValue) {
      this.clearTable()
      return
    }

    const body = new FormData()
    if (uploaded) body.append("csv_file", uploaded)
    this.appendAuthenticityToken(body)
    body.append("csv_has_headers", this.checkboxVal(this.headersTarget))
    body.append("col_sep", this.colSepTarget.value)
    body.append("quote_char", this.quoteCharTarget.value)
    body.append("liberal_parsing", this.checkboxVal(this.liberalTarget))
    body.append("strip", this.checkboxVal(this.stripCsvTarget))
    body.append("row_sep", this.rowSepTarget.value)

    fetch(this.previewUrlValue, { method: "POST", body, headers: this.csrfHeaders(), credentials: "same-origin" })
      .then((r) => r.json().then((data) => ({ ok: r.ok, status: r.status, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.error || "Preview failed")
        this.renderRows(data.rows || [])
      })
      .catch((e) => this.showError(e.message))
  }

  appendAuthenticityToken(formData) {
    const t = this.csrfToken()
    if (t) formData.append("authenticity_token", t)
  }

  csrfToken() {
    const el = document.querySelector('meta[name="csrf-token"]')
    return el?.getAttribute("content") || ""
  }

  csrfHeaders() {
    const t = this.csrfToken()
    return t ? { "X-CSRF-Token": t } : {}
  }

  checkboxVal(el) {
    return el?.checked ? "1" : "0"
  }

  hideError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.classList.add("d-none")
    this.errorTarget.textContent = ""
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
    this.clearTable()
  }

  clearTable() {
    if (this.hasPreviewHeadTarget) this.previewHeadTarget.innerHTML = ""
    if (this.hasPreviewBodyTarget) this.previewBodyTarget.innerHTML = ""
  }

  renderRows(rows) {
    if (!this.hasPreviewBodyTarget) return
    if (!rows.length) {
      this.clearTable()
      return
    }

    const keys = Object.keys(rows[0])
    this.previewHeadTarget.innerHTML =
      `<tr>${keys.map((k) => `<th class="small">${this.escapeHtml(k)}</th>`).join("")}</tr>`

    const body = rows
      .map(
        (row) =>
          `<tr>${keys.map((k) => `<td class="small">${this.escapeHtml(String(row[k] ?? ""))}</td>`).join("")}</tr>`
      )
      .join("")
    this.previewBodyTarget.innerHTML = body
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
